// Web counterpart to module/mini-app-store/native/api.ts.
// All persistent state lives in the mini-app-store's scoped localStorage keys
// (`@next-qr:mini-app-store:*`). Auth endpoints are hit via the user client,
// then tokens are persisted into the mini-app-store scope.

import {
  login as userLogin,
  register as userRegister,
  logout as userLogout,
  createGuest,
  forgotPassword as userForgotPassword,
  resetPassword as userResetPassword,
} from '@next-qr/openapi-client-user';
import * as userConfig from '@next-qr/openapi-client-user/config';
import {
  client as storeClient,
} from '@next-qr/openapi-client-mini-app-store';
import * as storeConfig from '@next-qr/openapi-client-mini-app-store/config';

export interface MiniAppInfo {
  id: number;
  app_id: string;
  name: string;
  description: string | null;
  icon: string | null;
  version: string;
  manifest: Record<string, unknown>;
  bundle_url: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface UserInfo {
  id: number;
  name: string;
  email: string | null;
  is_guest?: boolean;
}

export interface AuthResponse {
  token: string;
  refresh_token: string;
  refresh_token_expires_at: string;
  user: UserInfo;
}

export interface CheckResult {
  status: 'ok' | 'auth_required' | string;
  auth_required: boolean;
  authenticated: boolean;
  version: string;
  timestamp: string;
}

export interface UpdateCheckResult {
  app_id: string;
  latest_version: string;
  current_version: string | null;
  update_available: boolean;
  updated_at: string;
}

const API_PATH = '/api/mini-app-store';

/** When a refresh fails mid-session, prevent ensureGuestSession from silently
 * downgrading to a guest account. Reset on explicit logout. */
let _authSessionLost = false;

async function applyBaseURL(url: string): Promise<void> {
  await storeConfig.setBaseURL(url);
  await userConfig.setBaseURL(url);
}

async function persistAuth(data: AuthResponse): Promise<void> {
  await storeConfig.setAccessToken(data.token);
  await storeConfig.setRefreshToken(data.refresh_token);
  if (data.user) await storeConfig.setCurrentUser(data.user as unknown as Record<string, unknown>);
}

async function ensureGuestSession(): Promise<void> {
  if (storeConfig.getAccessToken()) return;
  if (_authSessionLost) return;
  const res = await createGuest({ throwOnError: false });
  if (res.data) await persistAuth(res.data as unknown as AuthResponse);
}

/**
 * Legacy no-op kept for backward compatibility. Each generated client now
 * auto-configures its own auth interceptors via its `config.ts` — callers no
 * longer need to wire them up here.
 * @deprecated Remove call sites; this function does nothing.
 */
export function configureClientWithAuth(_client?: unknown, _appId?: string): void {
  /* intentionally empty — each generated openapi-client auto-configures */
}

function parseApiError(data: Record<string, any>, fallback: string): string {
  const fieldErrors: string[] = (data.details ?? [])
    .flatMap((d: any) => d.errors ?? [])
    .map((e: any) => e.message as string)
    .filter(Boolean);
  if (fieldErrors.length > 0) return fieldErrors.join('\n');
  return (data as { error?: string }).error ?? fallback;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export interface ApiClient {
  get<T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T>;
  post<T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T>;
  put<T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T>;
  del<T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T>;
  url(path: string): Promise<string>;
  getApiBase(): Promise<string>;
  setApiBase(url: string | null): Promise<void>;
  getAccessToken(): Promise<string | null>;
  setAccessToken(token: string | null): Promise<void>;
  getRefreshToken(): Promise<string | null>;
  getUser(): Promise<UserInfo | null>;
  isGuest(): Promise<boolean>;
  login(credentials: { email: string; password: string }): Promise<UserInfo>;
  register(credentials: { email: string; name: string; password: string }): Promise<UserInfo>;
  forgotPassword(args: { email: string }): Promise<void>;
  resetPassword(args: { token: string; password: string }): Promise<UserInfo>;
  logout(): Promise<void>;
  check(): Promise<CheckResult>;
  fetchApps(status?: string): Promise<MiniAppInfo[]>;
  fetchAppDetail(appId: string): Promise<MiniAppInfo>;
  getBundleUrl(appId: string): Promise<string>;
  getCssBundleUrl(appId: string): Promise<string>;
  checkForUpdate(appId: string, currentVersion?: string): Promise<UpdateCheckResult>;
}

let _client: ApiClient | null = null;

export function getClient(): ApiClient {
  if (_client) return _client;

  async function url(path: string): Promise<string> {
    return `${storeConfig.getBaseURL()}${API_PATH}${path}`;
  }

  async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    await ensureGuestSession();
    const { method = 'GET', body, headers: extraHeaders = {}, signal } = options;
    try {
      const res = await storeClient.instance.request<T>({
        url:     `${API_PATH}${path}`,
        method,
        data:    body,
        headers: extraHeaders,
        signal,
      });
      return res.data;
    } catch (err: any) {
      const data = err.response?.data ?? {};
      throw new Error(parseApiError(data as Record<string, any>, `API error: ${err.response?.status ?? 'unknown'}`));
    }
  }

  _client = {
    url,
    async getApiBase() {
      return storeConfig.getBaseURL();
    },
    async setApiBase(u: string | null) {
      await applyBaseURL((u ?? '').trim().replace(/\/$/, ''));
    },
    async getAccessToken() {
      return storeConfig.getAccessToken();
    },
    async setAccessToken(token: string | null) {
      await storeConfig.setAccessToken(token);
    },
    async getRefreshToken() {
      return storeConfig.getRefreshToken();
    },
    async getUser() {
      return storeConfig.getCurrentUser<UserInfo>();
    },
    async isGuest() {
      const user = storeConfig.getCurrentUser<UserInfo>();
      return user ? !!user.is_guest : true;
    },

    async login({ email, password }) {
      const res = await userLogin({ body: { email, password }, throwOnError: true });
      const data = res.data as unknown as AuthResponse;
      await persistAuth(data);
      _authSessionLost = false;
      return data.user as UserInfo;
    },
    async register({ email, name, password }) {
      const res = await userRegister({ body: { email, name, password }, throwOnError: true });
      const data = res.data as unknown as AuthResponse;
      await persistAuth(data);
      _authSessionLost = false;
      return data.user as UserInfo;
    },
    async forgotPassword({ email }) {
      await userForgotPassword({ body: { email }, throwOnError: true });
    },
    async resetPassword({ token, password }) {
      const res = await userResetPassword({ body: { token, password }, throwOnError: true });
      const data = res.data as unknown as AuthResponse;
      await persistAuth(data);
      _authSessionLost = false;
      return data.user as UserInfo;
    },
    async logout() {
      const rt = storeConfig.getRefreshToken();
      if (rt) userLogout({ body: { refresh_token: rt }, throwOnError: false }).catch(() => {});
      await storeConfig.logout();
      _authSessionLost = false;
      await ensureGuestSession();
    },

    check:         () => request<CheckResult>('/check'),
    get:           <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
                     request<T>(path, { ...opts, method: 'GET' }),
    post:          <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
                     request<T>(path, { ...opts, method: 'POST', body }),
    put:           <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
                     request<T>(path, { ...opts, method: 'PUT', body }),
    del:           <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
                     request<T>(path, { ...opts, method: 'DELETE' }),
    fetchApps:     (status?: string) => {
      const query = status ? `?status=${encodeURIComponent(status)}` : '';
      return request<{ data: MiniAppInfo[] }>(`/${query}`).then(r => r.data);
    },
    fetchAppDetail:  (appId: string) => request<MiniAppInfo>(`/${encodeURIComponent(appId)}`),
    getBundleUrl:    (appId: string) => url(`/${encodeURIComponent(appId)}/bundle?platform=web`),
    getCssBundleUrl: (appId: string) => url(`/${encodeURIComponent(appId)}/bundle?platform=web-css`),
    checkForUpdate:  (appId: string, currentVersion?: string) => {
      const query = currentVersion ? `?version=${encodeURIComponent(currentVersion)}` : '';
      return request<UpdateCheckResult>(`/${encodeURIComponent(appId)}/check-update${query}`);
    },
  };

  return _client;
}
